import { defineStore } from "pinia";
import { bankService, activityService } from "@/services";
import router from "@/router";
import notifications from "@/resources/notifications";
import type { IBaseForm, IPagination, ISelectOption } from "@/types/general";
import type { IBank } from "@/types/response";
import type {
  IBankCreate,
  IBankUpdate,
  IInitialTransfer,
  ITransferBank,
  IUpdateActivity,
} from "@/types/requests";
import { ref, type Ref } from "vue";
import { parseCurrency } from "@/utils/masks";

interface State {
  banks?: IBank[];
  pagination: IPagination;
  bankForm: IBaseForm;
  bankDetail: IBank;
  banksSelection: ISelectOption[];
  isTransferModalOpened: Ref<boolean>;
  transferForm: IBaseForm;
  totalBalanceTl: string;
  totalBalanceUsd: string;
  isAddModalOpened: Ref<boolean>;
  searchForm: IBaseForm;
  addMoneyForm: IBaseForm;
  timeoutValue: number;
  isBankEditModalOpened: Ref<boolean>;
  editBankForm: IBaseForm;
  isInitialAddEdit: boolean;
  isTransferEdit: boolean;
  bankActivityId: string;
}
export const useBankStore = defineStore("banks", {
  state: () =>
    ({
      pagination: {
        page: 1,
        totalCount: 0,
        pageSize: 20,
        totalPages: 0,
      },
      bankForm: {} as IBaseForm,
      bankDetail: {} as IBank,
      banksSelection: [] as ISelectOption[],
      isTransferModalOpened: ref(false),
      isBankEditModalOpened: ref(false),
      isAddModalOpened: ref(false),
      transferForm: {} as IBaseForm,
      searchForm: {} as IBaseForm,
      addMoneyForm: {} as IBaseForm,
      editBankForm: {} as IBaseForm,
    }) as State,
  getters: {},
  actions: {
    formatBanks(data: IBank[]) {
      return data.map((el) => {
        return {
          ...el,
          key: el.id,
        };
      });
    },

    async getBanks(
      pageNumber?: number,
      pageSize?: number,
      isSelection?: boolean,
      isSelectionFilterForTransfer?: boolean
    ) {
      const page = pageNumber ?? this.pagination.page;
      const size = pageSize ?? this.pagination.pageSize;
      const data = await bankService.getBanks(
        page,
        size,
        this.searchForm.search === undefined
          ? ""
          : this.searchForm.search.value
            ? this.searchForm.search.value
            : ""
      );
      if (data) {
        if (data?.banks?.length) {
          this.pagination.totalCount = data.totalRecords;
          this.pagination.totalPages = data.totalPages;
          if (isSelection) {
            if (isSelectionFilterForTransfer) {
              this.banksSelection = data.banks
                .filter((el) => el.id !== this.bankDetail.id)
                .map((el) => {
                  return {
                    value: el.id,
                    key: el.id,
                    selected: false,
                    label: el.bankName,
                    selectedValue: el.currency,
                  };
                });
            } else {
              this.banksSelection = data.banks.map((el) => {
                return {
                  value: el.id,
                  key: el.id,
                  selected: false,
                  label: el.bankName,
                  selectedValue: el.currency,
                };
              });
            }
          } else {
            this.banks = this.formatBanks(data?.banks);
          }
        } else if (isSelection) {
          this.banksSelection = [];
        } else {
          // Arama sonuç vermediyse önceki liste ekranda kalmaz.
          this.banks = [];
          this.pagination.page = 1;
          this.pagination.totalCount = 0;
          this.pagination.totalPages = 0;
        }
        this.totalBalanceTl = data!.totalBalanceTl;
        this.totalBalanceUsd = data!.totalBalanceUsd;
      } else {
        this.banks = [];
        this.pagination = {
          page: 1,
          totalCount: 0,
          pageSize: 20,
          totalPages: 0,
        };
      }
    },

    async deleteBank(id: string) {
      if (!id) {
        return;
      }
      const { success, error } = await bankService.deleteBank(id);
      if (success) {
        notifications.success.transfer();
        return true;
      }
      notifications.error.generalError(error);
      return false;
    },

    async createBank() {
      const requestBody: IBankCreate = {
        bankName: this.bankForm.bankName.value,
        currencyType: this.bankForm.currencyType.value,
        iban: this.bankForm.iban.value,
        accountName: this.bankForm.accountName.value,
        accountNumber: this.bankForm.accountNumber.value,
        branchCode: this.bankForm.branchCode.value,
        bank: this.bankForm.bank.value,
      };
      const { success, error } = await bankService.createBank(requestBody);
      if (success) {
        notifications.success.createBank();
        await router.push({ name: "BanksView" });
      } else {
        notifications.error.generalError(error);
      }
    },

    async setInitialTemplate(bankId: string) {
      if (bankId) {
        const data = await bankService.getBankById(bankId);
        const targetBank = data?.bank;
        if (targetBank) {
          this.bankDetail! = targetBank;
        }
      }
    },

    async transfer() {
      const rate = parseCurrency(this.transferForm.rate.value);
      const requestBody: ITransferBank = {
        amountTl:
          this.transferForm.amountTl.value === ""
            ? 0
            : parseCurrency(this.transferForm.amountTl.value),
        amountUsd:
          this.transferForm.amountUsd.value === ""
            ? 0
            : parseCurrency(this.transferForm.amountUsd.value),
        rate: rate === "" ? 0 : rate,
        description: this.transferForm.description.value,
      };
      const bankId = this.transferForm.bank.props.options.find(
        (option) => option.selected === true
      ).key;
      const { success, error } = await bankService.transfer(
        this.bankDetail.id,
        bankId,
        requestBody
      );
      if (success) {
        notifications.success.transfer();
        this.isTransferModalOpened = false;
        this.setInitialTemplate(this.bankDetail.id);
      } else {
        notifications.error.generalError(error);
      }
    },

    async initialTransfer() {
      const requestBody: IInitialTransfer = {
        amount: parseCurrency(this.addMoneyForm.amount.value),
        description: this.addMoneyForm.description.value,
      };
      const { success, error } = await bankService.initialTransfer(
        this.bankDetail.id,
        requestBody
      );
      if (success) {
        notifications.success.initialTransfer();
        this.isAddModalOpened = false;
        this.setInitialTemplate(this.bankDetail.id);
      } else {
        notifications.error.generalError(error);
      }
    },

    async updateBank() {
      const requestBody: IBankUpdate = {
        accountName: this.editBankForm.accountName.value,
        accountNumber: this.editBankForm.accountNumber.value,
        iban: this.editBankForm.iban.value,
        branchCode: this.editBankForm.branchCode.value,
        bank: this.editBankForm.bank.value,
        bankName: this.editBankForm.bankName.value,
      };
      const { success, error } = await bankService.updateBank(
        this.bankDetail.id,
        requestBody
      );
      if (success) {
        notifications.success.updateBank();
        this.isBankEditModalOpened = false;
        this.setInitialTemplate(this.bankDetail.id);
      } else {
        notifications.error.generalError(error);
      }
    },

    async updateActivity(type: string) {
      let requestBody: IUpdateActivity;
      if (type === "initial") {
        requestBody = {
          amount: parseCurrency(this.addMoneyForm.amount.value),
          description: this.addMoneyForm.description.value,
        };
      } else {
        const bank = this.transferForm.bank.props.options.find(
          (option) => option.selected === true
        );
        if (bank?.selectedValue === "USD") {
          if (this.transferForm.rate.value === "") {
            requestBody = {
              amount: parseCurrency(this.transferForm.amountUsd.value),
              description: this.transferForm.description.value,
            };
          } else {
            requestBody = {
              amount: parseCurrency(this.transferForm.amountUsd.value),
              description: this.transferForm.description.value,
              rate: parseCurrency(this.transferForm.rate.value),
              revertAmountForRent: parseCurrency(
                this.transferForm.amountTl.value
              ),
            };
          }
        } else {
          if (this.transferForm.rate.value === "") {
            requestBody = {
              amount: parseCurrency(this.transferForm.amountTl.value),
              description: this.transferForm.description.value,
            };
          } else {
            requestBody = {
              amount: parseCurrency(this.transferForm.amountTl.value),
              description: this.transferForm.description.value,
              rate: parseCurrency(this.transferForm.rate.value),
              revertAmountForRent: parseCurrency(
                this.transferForm.amountUsd.value
              ),
            };
          }
        }
      }

      const result = await activityService.updateActivity(
        this.bankActivityId,
        requestBody
      );
      if (result.success) {
        notifications.success.updateActivity();
        this.setInitialTemplate(this.bankDetail.id);
        this.isAddModalOpened = false;
        this.isTransferModalOpened = false;
      } else {
        notifications.error.generalError(result.error);
      }
    },
  },
});
